import { useCallback, useEffect, useState } from "react";

import { getProspectUuid, getProspectCanonicalId } from "utils/idHelpers";
import { isTestUser } from "utils/helper";
import {
  useGetStepQuery,
  useCreateApplicationMutation,
} from "types/generated/operations";
import Loader from "uicomponents/Loader";
import Error from "uicomponents/Error";
import { trackFormError } from "utils/analytics/trackEvent";
import WorkerCompApplicationTestVariant from "./WorkerCompApplicationTestVariant";
import WorkerCompApplicationStepVariant from "./WorkersCompStepVariant";
import WorkerCompApplicationCoreAppVariant from "./WorkerCompApplicationCoreAppVariant";
import {
  WORKERS_COMPENSATION_STEPS,
  WORKERS_COMPENSATION_STEPS_WITH_CORE_APP,
} from "utils/constants";
import { hasQueryParam } from "utils/helper";
import { generateAppStepUrl } from "utils/stepApiHelpers";
import useFetch from "utils/useFetch";
import { useOktaAuth } from "@okta/okta-react";

const WorkerCompApplicationModel = () => {
  const { data, error, loading, refetch } = useGetStepQuery({
    variables: { uuid: getProspectUuid() },
    fetchPolicy: "no-cache",
    skip: !getProspectUuid() && !isTestUser,
  });

  const wc_flow_with_core_app = hasQueryParam("usecoreapp");
  const stepList = wc_flow_with_core_app
    ? WORKERS_COMPENSATION_STEPS_WITH_CORE_APP
    : WORKERS_COMPENSATION_STEPS;
  const [isNewApplication, setIsNew] = useState(false);

  const { authState } = useOktaAuth();

  const stepUrl = generateAppStepUrl();
  const {
    data: stepData,
    error: fetchStepsError,
    loading: stepLoading,
  }: {
    data: any;
    error: any;
    loading: boolean;
    refetch: () => void;
  } = useFetch(stepUrl, {
    authToken: authState?.accessToken?.accessToken,
  });

  const [createApplication, { loading: createApplicationLoading }] =
    useCreateApplicationMutation();

  const createNewApplication = useCallback(async () => {
    setIsNew(true);
    try {
      await createApplication({
        variables: {
          prospectId: getProspectUuid(),
          prospectCanonicalId: getProspectCanonicalId(),
          applicationSteps: stepList,
        },
      });

      refetch();
    } catch (error: any) {
      trackFormError(error, "error creating application");

      return <Error />;
    }
  }, [createApplication, refetch, stepList]);

  useEffect(() => {
    if (!data?.prospect?.applications?.length && !loading && !error) {
      // if a prospect does not have an application, create one
      createNewApplication();
    }
  }, [data, error, loading, createNewApplication]);

  if (stepLoading) {
    return <Loader message="Getting your application" />;
  }

  if (error || fetchStepsError) {
    if (error) trackFormError(error, "error getting application status");
    return <Error />;
  }

  const renderNewFlow =
    (data?.prospect?.applications?.[0]?.flowVersion === "v1" &&
      stepData?.steps?.length) ||
    isNewApplication ||
    loading;

  if (wc_flow_with_core_app) {
    return (
      <WorkerCompApplicationCoreAppVariant
        applicationUuid={data?.prospect?.applications?.[0]?.uuid}
        stepData={stepData}
      />
    );
  }

  if (renderNewFlow) {
    return (
      <WorkerCompApplicationStepVariant
        applicationUuid={data?.prospect?.applications?.[0]?.uuid}
        stepData={stepData}
      />
    );
  }

  if (!data?.prospect?.applications?.length) {
    return <Loader />;
  }

  if (createApplicationLoading) {
    return <Loader message="Creating your application" />;
  }

  return <WorkerCompApplicationTestVariant aplicationStatus={data?.prospect} />;
};
export default WorkerCompApplicationModel;
